import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { backdropVariants, clayModalVariants, clayTapScale } from '../../constants/animations';
import styles from './Modal.module.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Pointer handlers attached to the full-viewport backdrop (e.g. for swipe-to-navigate). */
  backdropPointerHandlers?: Pick<
    React.HTMLAttributes<HTMLDivElement>,
    'onPointerDown' | 'onPointerUp' | 'onPointerCancel'
  >;
}

const sizeClassMap = {
  sm:   styles['size-sm'],
  md:   styles['size-md'],
  lg:   styles['size-lg'],
  xl:   styles['size-xl'],
  '2xl': styles['size-2xl'],
  full: styles['size-full'],
};

// Module-level stack of currently-open Modal close handlers, ordered from
// oldest (bottom) to newest (top). On Escape we only close the topmost Modal
// — otherwise nesting (e.g. preview modal inside the album-creation wizard)
// would close both at once.
const modalStack: Array<() => void> = [];

export function Modal({ isOpen, onClose, title, children, size = 'md', backdropPointerHandlers }: ModalProps) {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Push/pop this modal on the global stack while it's open
  useEffect(() => {
    if (!isOpen) return;
    modalStack.push(onClose);
    return () => {
      const idx = modalStack.lastIndexOf(onClose);
      if (idx >= 0) modalStack.splice(idx, 1);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen) return;
      // Only the topmost open modal reacts to Escape.
      if (modalStack[modalStack.length - 1] !== onClose) return;
      onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.backdrop}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          style={backdropPointerHandlers ? { touchAction: 'none' } : undefined}
          {...backdropPointerHandlers}
        >
          <motion.div
            className={[styles.panel, sizeClassMap[size]].join(' ')}
            variants={clayModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {title && (
              <div className={styles.header}>
                <h2 className={styles.title}>{title}</h2>
                <motion.button
                  onClick={onClose}
                  className={styles['close-btn']}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={clayTapScale}
                  transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                >
                  <svg className={styles['close-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>
            )}
            <div className={size === 'full' ? styles['scroll-body'] : ''}>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
